import React from 'react';
import { X, CreditCard, Crown, Sparkles, Gift, Diamond, Loader2 } from 'lucide-react';
import { useTransaction } from '../../hooks/useTransaction';

interface CreditPurchaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const creditOptions = [
  {
    amount: 500,
    algos: 5,
    icon: CreditCard,
    title: 'Starter',
    description: 'Perfect for trying out the service',
    color: 'from-emerald-500 to-teal-500',
    shadowColor: 'emerald',
    popular: false,
    layout: 'half'
  },
  {
    amount: 1000,
    algos: 10,
    icon: Sparkles,
    title: 'Basic',
    description: 'Great for casual users',
    color: 'from-blue-500 to-indigo-500',
    shadowColor: 'blue',
    popular: false,
    layout: 'half'
  },
  {
    amount: 2500,
    algos: 25,
    icon: Crown,
    title: 'Pro',
    description: 'Unlock daily AI assistance',
    color: 'from-violet-500 to-purple-500',
    glowColor: 'violet',
    popular: true,
    layout: 'half'
  },
  {
    amount: 10000,
    algos: 100,
    icon: Gift,
    title: 'Business',
    description: 'Advanced usage for growing teams',
    color: 'from-orange-500 to-pink-500',
    shadowColor: 'orange',
    popular: false,
    layout: 'full'
  },
  {
    amount: 100000,
    algos: 1000,
    icon: Diamond,
    title: 'Enterprise',
    description: 'Extensive usage of coding and reasoning',
    color: 'from-rose-500 to-red-500',
    shadowColor: 'rose',
    popular: false,
    layout: 'full'
  }
];

export const CreditPurchaseDialog: React.FC<CreditPurchaseDialogProps> = ({
  isOpen,
  onClose
}) => {
  const { handleCreditPurchase, isLoading } = useTransaction();
  if (!isOpen) return null;

  // Group options by layout
const halfWidthOptions = creditOptions.slice(0, 3); // First 3 options (5, 10, and 25 ALGO)
const fullWidthOptions = creditOptions.slice(3); // Remaining options

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 w-full h-screen flex flex-col">
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Purchase Credits</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Choose a credit package that suits your needs
            </p>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-custom">
            <div className="p-8 space-y-8 max-w-7xl mx-auto">
              {/* Half-width options in a grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {halfWidthOptions.map((option, index) => (
                  <div
                    key={option.amount}
                    className={`
                      relative group
                      ${option.popular ? 'z-10' : ''}
                    `}
                  >
                    {option.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-violet-500 text-white px-4 py-1 rounded-full text-sm font-medium z-20 shadow-lg shadow-violet-500/25">
                        Most Popular
                      </div>
                    )}
                    <div
                      className={`
                        relative overflow-hidden rounded-xl transition-all duration-300
                        bg-gradient-to-br ${option.color}
                        cursor-pointer
                        ${option.popular 
                          ? 'scale-105 hover:scale-110 shadow-2xl shadow-violet-500/50 animate-pulse-subtle' 
                          : 'group-hover:scale-[1.02] hover:shadow-lg'
                        }
                      `}
                      onClick={() => !isLoading && handleCreditPurchase(option.algos)}
                    >
                      {/* Animated shine effect */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine" />
                      </div>

                      {/* Glow effect for popular option */}
                      {option.popular && (
                        <div className="absolute inset-0 bg-violet-500 opacity-20 blur-xl animate-glow" />
                      )}

                      <div className="relative p-8 text-white">
                      {isLoading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm z-10">
                          <Loader2 className="w-8 h-8 animate-spin text-white" />
                        </div>
                      )}
                        <div className="flex items-center justify-between mb-4">
                          <option.icon className={`w-8 h-8 ${option.popular ? 'animate-bounce-subtle' : ''}`} />
                          <div className="text-2xl font-bold">{option.algos} ALGO</div>
                        </div>

                        <h3 className="text-xl font-bold mb-2">{option.title}</h3>
                        <p className="text-white/90 text-sm mb-4">{option.description}</p>

                        <div className="text-sm font-medium">
                          Get {option.amount} credits
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Full-width options */}
              {fullWidthOptions.map((option) => (
                <div
                  key={option.amount}
                  className={`
                    relative group 
                    ${option.popular ? 'z-10' : ''}
                  `}
                >
                  {option.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-violet-500 text-white px-4 py-1 rounded-full text-sm font-medium z-20 shadow-lg shadow-violet-500/25">
                      Most Popular
                    </div>
                  )}

                  <div
                    className={`
                      relative overflow-hidden rounded-xl transition-all duration-300
                      bg-gradient-to-br ${option.color}
                      cursor-pointer
                      ${option.popular 
                        ? 'scale-105 hover:scale-110 shadow-2xl shadow-violet-500/50 animate-pulse-subtle' 
                        : 'group-hover:scale-[1.02] hover:shadow-lg'
                      }
                    `}
                    onClick={() => !isLoading && handleCreditPurchase(option.algos)}
                  >
                    {/* Animated shine effect */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine" />
                    </div>

                    {/* Glow effect for popular option */}
                    {option.popular && (
                      <div className="absolute inset-0 bg-violet-500 opacity-20 blur-xl animate-glow" />
                    )}

                    <div className="relative p-8 text-white md:flex md:items-center md:justify-between">
                      {isLoading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm z-10">
                          <Loader2 className="w-8 h-8 animate-spin text-white" />
                        </div>
                      )}
                      <div className="md:flex-1">
                        <div className="flex items-center space-x-4 mb-4 md:mb-0">
                          <option.icon className={`w-8 h-8 ${option.popular ? 'animate-bounce-subtle' : ''}`} />
                        </div>

                        <h3 className="text-xl font-bold mb-2">{option.title}</h3>
                        <p className="text-white/90 text-sm mb-4">{option.description}</p>

                        <div className="text-sm font-medium">
                          Get {option.amount} credits
                        </div>
                      </div>

                      <div className="mt-4 md:mt-0 md:ml-8 md:flex md:items-center md:flex-shrink-0">
                        <div className="text-3xl font-bold">{option.algos} ALGO</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="px-8 py-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              By purchasing credits, you agree to our terms of service. Credits are non-refundable.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
